import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
} from '@mui/material';
import { SwipeLeftAlt, InfoOutlined } from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';
import { getComparator } from '../../utils/tableSort';
import PartnerDrilldownModal from './PartnerDrilldownModal';

const dividerSx = { borderLeft: '2px solid', borderLeftColor: 'divider' };

const fmtPct = (v) => (v != null ? `${v}%` : '—');

const CohortCell = ({ value, sampleClicks, threshold = 20 }) => {
  if (sampleClicks < threshold || value == null) {
    return (
      <Tooltip title={`Not enough data (${sampleClicks} clicks in this period, need ${threshold}+)`} arrow>
        <span style={{ cursor: 'help', borderBottom: '1px dashed currentColor', color: 'inherit' }}>—</span>
      </Tooltip>
    );
  }
  return <span>{value}%</span>;
};

// Column header with optional tooltip
const ColHeader = ({ label, tooltip, sortProps }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
    <TableSortLabel {...sortProps}>{label}</TableSortLabel>
    {tooltip && (
      <Tooltip title={tooltip} arrow>
        <InfoOutlined sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
      </Tooltip>
    )}
  </Box>
);

// Column definitions — grouped as: received (volume → rate → benchmark) | sent (volume → rate → benchmark) | economics
const baseColumns = [
  { id: 'partner_name', label: 'Partner', align: 'left', sx: { minWidth: 120 } },
];

const receivedColumns = [
  { id: 'clicks_received',            label: 'Clicks recv.',     align: 'right' },
  { id: 'orders_acquired',            label: 'Orders recv.',     align: 'right' },
  { id: 'inbound_conversion_pct',     label: 'Conv %',           align: 'right' },
];

const sentColumns = [
  { id: 'clicks_sent',                label: 'Clicks sent',      align: 'right', sx: dividerSx },
  { id: 'orders_sent',                label: 'Orders sent',      align: 'right' },
  { id: 'outbound_conversion_pct',    label: 'Conv %',           align: 'right' },
];

const econColumns = [
  { id: 'affiliate_revenue_earned',   label: 'Aff. earned',      align: 'right', sx: dividerSx },
  { id: 'net_acquisition_cost',       label: 'Net cost',         align: 'right' },
];

const legacyColumns = [
  { id: 'clicks',          label: 'Clicks received', align: 'right' },
  { id: 'orders_acquired', label: 'Orders acquired', align: 'right' },
  { id: 'conversion_rate', label: 'Conv %',          align: 'right' },
];

function renderCell(col, row) {
  const v = row[col.id];
  if (col.id === 'cohort_inbound_conversion_pct') {
    return <CohortCell value={v} sampleClicks={row.cohort_inbound_sample_clicks || 0} />;
  }
  if (col.id === 'cohort_outbound_conversion_pct') {
    return <CohortCell value={v} sampleClicks={row.cohort_outbound_sample_clicks || 0} />;
  }
  if (['inbound_conversion_pct', 'outbound_conversion_pct', 'conversion_rate'].includes(col.id)) {
    return fmtPct(v);
  }
  if (['affiliate_revenue_earned', 'net_acquisition_cost', 'revenue_generated', 'acquisition_spend', 'cpo', 'aov'].includes(col.id)) {
    return formatCurrency(v);
  }
  if (['clicks_received', 'clicks_sent', 'clicks', 'orders_acquired', 'orders_sent'].includes(col.id)) {
    return (v || 0).toLocaleString();
  }
  return v ?? '—';
}

const PartnerPerformanceTable = ({ partners, trafficByPartner, startDate, endDate, myRetailerId }) => {
  const [orderBy, setOrderBy] = useState('orders_acquired');
  const [order, setOrder] = useState('desc');
  const [drilldownPartner, setDrilldownPartner] = useState(null);

  const hasEnrichedTraffic = Array.isArray(trafficByPartner) &&
    trafficByPartner.length > 0 &&
    trafficByPartner[0]?.clicks_received !== undefined;

  const hasLegacyTraffic = Array.isArray(trafficByPartner) && trafficByPartner.length > 0 && !hasEnrichedTraffic;

  const columns = useMemo(() => [
    ...baseColumns,
    ...(hasEnrichedTraffic ? receivedColumns : hasLegacyTraffic ? legacyColumns : [{ id: 'orders_acquired', label: 'Orders recv.', align: 'right' }]),
    ...(hasEnrichedTraffic ? sentColumns : []),
    ...econColumns,
  ], [hasEnrichedTraffic, hasLegacyTraffic]);

  const handleSort = (columnId) => {
    const isDesc = orderBy === columnId && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(columnId);
  };

  const mergedPartners = useMemo(() => {
    if (!partners) return [];
    if (!trafficByPartner?.length) return partners;
    const trafficMap = {};
    for (const t of trafficByPartner) trafficMap[t.partner_id] = t;
    // /dashboard/summary is the authority for order counts and money (Aff. earned,
    // Net cost); /traffic-stats only adds click and conversion fields. Spread order
    // matters: a traffic row must never overwrite a summary value.
    return partners.map((p) => ({ ...(trafficMap[p.partner_id] || {}), ...p }));
  }, [partners, trafficByPartner]);

  const sorted = useMemo(
    () => [...mergedPartners].sort(getComparator(order, orderBy)),
    [mergedPartners, order, orderBy]
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
        Partner performance
      </Typography>
      {sorted.length === 0 ? (
        <Typography color="textSecondary">No partner data for this period.</Typography>
      ) : (
        <>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', mb: 0.5 }}>
            <SwipeLeftAlt sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">Scroll for more</Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { px: 1.5 }, '& td': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align} sx={{ ...col.sx, verticalAlign: 'bottom', pb: 1 }}>
                      <ColHeader
                        label={col.label}
                        tooltip={col.tooltip}
                        sortProps={{
                          active: orderBy === col.id,
                          direction: orderBy === col.id ? order : 'desc',
                          onClick: () => handleSort(col.id),
                        }}
                      />
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 24 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow
                    key={row.partner_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDrilldownPartner(row)}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align} sx={col.sx}>
                        {renderCell(col, row)}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ color: 'text.disabled', fontSize: 12, pr: 1 }}>↗</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {drilldownPartner && (
        <PartnerDrilldownModal
          open={!!drilldownPartner}
          partner={drilldownPartner}
          onClose={() => setDrilldownPartner(null)}
          startDate={startDate}
          endDate={endDate}
          myRetailerId={myRetailerId}
        />
      )}
    </Box>
  );
};

export default PartnerPerformanceTable;
